import { useEffect, useState } from 'react';
import React from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { TalkJSConfiguration } from './chat-tabs/TalkJSConfiguration';
import { CustomersConversations } from './chat-tabs/CustomersConversations';
import { SupportSchedule } from './chat-tabs/SupportSchedule';
import { AiTabs, AiTab } from '../AiTabs';
import { AiReportButton } from '../AiReportButton';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectIsFeatureEnabled } from '../../store/models/Organization';
import { Features } from '../../constants';

const TAB_CONFIG = {
  configuration: {
    label: 'TalkJS Configuration',
    description: 'Configure TalkJS settings and integration'
  },
  conversations: {
    label: 'Conversations',
    description: 'View and manage customer conversations'
  },
  schedule: {
    label: 'Schedule',
    description: 'Configure realtime support hours'
  }
};

export const RagChatsTab = ({ chatbot, projectId, isMini = false, userRole = null, accessToken = null }) => {
  const isAiReportEnabled = useSelector(state => selectIsFeatureEnabled(state, Features.RAG_AI_REPORT));
  const [conversationKey, setConversationKey] = useState(0);
  const currentDate = new Date();
  const iniStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
  const [startDate, setStartDate] = useState(iniStartDate);
  const [endDate, setEndDate] = useState(currentDate);
  
  // Check if TalkJS is already configured
  const isConfigurationSaved = React.useMemo(() => {
    const appId = _.get(chatbot, 'ui.talkjs.app_id', '');
    const secretKey = _.get(chatbot, 'ui.talkjs.secret_key', '');
    const enabled = _.get(chatbot, 'ui.talkjs.enabled', false);
    return enabled && appId && secretKey;
  }, [chatbot]);

  // Set default tab based on configuration status
  const [activeTab, setActiveTab] = useState(() => {
    if (isMini) {
      return 'conversations'; // Always default to conversations for mini view
    }
    return isConfigurationSaved ? 'conversations' : 'configuration';
  });


  // Update active tab when configuration changes
  useEffect(() => {
    if (isMini) {
      // In mini mode, always stay on conversations tab
      if (activeTab !== 'conversations' && activeTab !== 'schedule') {
        setActiveTab('conversations');
      }
      return;
    }
    
    // For regular mode, allow free navigation - don't force tab switches
    // Only auto-switch if configuration is not saved and user tries to access conversations
    if (!isConfigurationSaved && activeTab === 'conversations') {
      setActiveTab('configuration');
    }
  }, [isConfigurationSaved, isMini, activeTab]);

  // Handle configuration saved - switch to conversations tab and refresh
  const handleConfigurationSaved = () => {
    setActiveTab('conversations');
    // Force refresh of conversations component
    setConversationKey(prev => prev + 1);
  };


  return (
    <div className='AiServicePage__tab-content'>
      <div className='RagChatbotDetailPage chats'>
        <AiTabs>
          {Object.entries(TAB_CONFIG)
            .filter(([key]) => {
              // For mini dashboard, hide configuration tab
              if (isMini && key === 'configuration') return false;
              // For rep role, only show conversations tab
              if (userRole === 'rep' && key !== 'conversations') return false;
              return true;
            })
            .map(([key, { label }]) => (
            <AiTab
              key={key}
              isSelected={activeTab === key}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </AiTab>
          ))}
        </AiTabs>

        {activeTab === 'configuration' && (
          <TalkJSConfiguration 
            chatbot={chatbot} 
            projectId={projectId} 
            onConfigurationSaved={handleConfigurationSaved}
            bypassLlmCheck={isMini}
          />
        )}

        {activeTab === 'conversations' && (
          <>
            {isAiReportEnabled && (
              <div className='RagChatbotDetailPage__row' style={{ marginBottom: '20px', justifyContent: 'flex-end' }}>
                <AiReportButton
                  chatbotId={chatbot?.id}
                  projectId={projectId}
                  accessToken={accessToken}
                  startDate={startDate}
                  endDate={endDate}
                  buttonTitle="AI Report"
                  buttonColor="blue-outline"
                  buttonSize="medium"
                />
              </div>
            )}
            <CustomersConversations 
              key={conversationKey}
              chatbot={chatbot}
              projectId={projectId}
              userRole={userRole}
            />
          </>
        )}

        {activeTab === 'schedule' && (
          <SupportSchedule 
            chatbot={chatbot}
            projectId={projectId}
          />
        )}
      </div>
    </div>
  );
};